import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { api } from '@/lib/api';
import { SITE_TYPE_LABELS, apiErrorMessage } from '@/lib/clients';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import type { Site, SiteType } from '@logistech/ui-shared';

const coord = (min: number, max: number) =>
  z.string().trim().refine((v) => v === '' || (!Number.isNaN(Number(v)) && Number(v) >= min && Number(v) <= max), {
    message: `Valeur entre ${min} et ${max}`,
  });

// Aligné sur le schema Zod du missions-service (routes/sites/index.ts)
const schema = z
  .object({
    name: z.string().trim().min(2, 'Nom requis (2 caractères min.)').max(100),
    type: z.enum(['MINE', 'PORT', 'WAREHOUSE', 'CLIENT', 'DEPOT', 'OTHER']),
    address: z.string().max(200),
    city: z.string().max(100),
    lat: coord(-90, 90),
    lon: coord(-180, 180),
    contactName: z.string().max(100),
    contactPhone: z.string().max(30),
  })
  .refine((d) => (d.lat === '') === (d.lon === ''), {
    message: 'Renseigner latitude et longitude ensemble',
    path: ['lon'],
  });

type FormData = z.infer<typeof schema>;

const TEXT_FIELDS = ['address', 'city', 'contactName', 'contactPhone'] as const;

const toPayload = (d: FormData, clientId: string, isEdit: boolean) => {
  const empty = isEdit ? null : undefined;
  const payload: Record<string, unknown> = { name: d.name, type: d.type, clientId };
  for (const f of TEXT_FIELDS) payload[f] = d[f].trim() === '' ? empty : d[f].trim();
  payload['lat'] = d.lat === '' ? empty : Number(d.lat);
  payload['lon'] = d.lon === '' ? empty : Number(d.lon);
  return payload;
};

const toDefaults = (site?: Site): FormData => ({
  name: site?.name ?? '',
  type: site?.type ?? 'CLIENT',
  address: site?.address ?? '',
  city: site?.city ?? '',
  lat: site?.lat?.toString() ?? '',
  lon: site?.lon?.toString() ?? '',
  contactName: site?.contactName ?? '',
  contactPhone: site?.contactPhone ?? '',
});

interface Props {
  clientId: string;
  /** Site à modifier ; absent = création */
  site?: Site;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SiteDialog({ clientId, site, open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const isEdit = !!site;

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormData>({ resolver: zodResolver(schema), defaultValues: toDefaults(site) });

  useEffect(() => {
    if (open) reset(toDefaults(site));
  }, [open, site, reset]);

  const mutation = useMutation({
    mutationFn: (d: FormData) =>
      isEdit
        ? api.patch(`/sites/${site.id}`, toPayload(d, clientId, true))
        : api.post('/sites', toPayload(d, clientId, false)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['client', clientId] });
      void qc.invalidateQueries({ queryKey: ['sites'] });
      toast.success(isEdit ? 'Site modifié' : 'Site ajouté');
      onOpenChange(false);
    },
    onError: (err: unknown) => toast.error(apiErrorMessage(err, "Erreur lors de l'enregistrement du site")),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Modifier le site' : 'Nouveau site'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit((d) => mutation.mutate(d))} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="site-name">Nom *</Label>
              <Input id="site-name" {...register('name')} placeholder="Mine de Sangarédi" />
              {errors.name && <p className="text-xs text-danger mt-1">{errors.name.message}</p>}
            </div>
            <div>
              <Label htmlFor="site-type">Type</Label>
              <Select value={watch('type')} onValueChange={(v) => setValue('type', v as SiteType)}>
                <SelectTrigger id="site-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(SITE_TYPE_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="site-address">Adresse</Label>
              <Input id="site-address" {...register('address')} />
            </div>
            <div>
              <Label htmlFor="site-city">Ville</Label>
              <Input id="site-city" {...register('city')} placeholder="Boké" />
            </div>
            <div>
              <Label htmlFor="site-lat">Latitude</Label>
              <Input id="site-lat" inputMode="decimal" {...register('lat')} placeholder="10.9336" />
              {errors.lat && <p className="text-xs text-danger mt-1">{errors.lat.message}</p>}
            </div>
            <div>
              <Label htmlFor="site-lon">Longitude</Label>
              <Input id="site-lon" inputMode="decimal" {...register('lon')} placeholder="-14.2917" />
              {errors.lon && <p className="text-xs text-danger mt-1">{errors.lon.message}</p>}
            </div>
            <div>
              <Label htmlFor="site-contactName">Contact sur site</Label>
              <Input id="site-contactName" {...register('contactName')} />
            </div>
            <div>
              <Label htmlFor="site-contactPhone">Téléphone du contact</Label>
              <Input id="site-contactPhone" {...register('contactPhone')} />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Les coordonnées GPS servent au calcul d&apos;itinéraire et au démarrage automatique des missions.
          </p>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Enregistrement…' : isEdit ? 'Enregistrer' : 'Ajouter le site'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
